import { Capacitor } from "@capacitor/core"
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import _ from "lodash"
import { err, ok, Result } from "neverthrow"

export enum LdbBlobStorageEncoding {
	// when the data is text, passing strings around works everywhere
	Text = 'text',
	// sql.js in the browser can deal with Uint8Arrays just fine
	Bin = 'bin',
	// with @capacitor-community/sqlite, seems we need to store binaries
	// as base64 text.  see also CapacitorSqlRuntimeTests.ts
	// future alternatives: https://capawesome.io/plugins/sqlite ($$)
	// or storing binaries on the iOS filesystem instead
	B64Bin = 'b64Bin',
}

// expect `data` argument to be of type
// * Uint8Array when coming from sql.js always due to BLOB column affinity;
// * string when from @capacitor-community/sqlite due to
//   `cast (data as text)` in select statement
export const ldbReadBlobData = (data: string|Uint8Array, encoding: LdbBlobStorageEncoding): Result<Uint8Array, FusionError> => {
	switch(encoding) {
		case LdbBlobStorageEncoding.B64Bin:
			try {
				// @ts-ignore  this only works in safari/iOS
				const bin: Uint8Array = Uint8Array.fromBase64(data)
				return ok(bin)
			} catch(ex) {
				return err(serializeFusionError('ldbReadBlobData', ex))
			}
		default:
			if(_.isString(data)) {
				return ok(new TextEncoder().encode(data))
			} else if(data instanceof Uint8Array) {
				return ok(data)
			} else {
				return err(serializeFusionError('ldbReadBlobData', `unexpected data type for encoding '${encoding}'`))
			}
	}
}

export const ldbReadBlobPrettyJson = (data: string|Uint8Array, encoding: LdbBlobStorageEncoding): string => {
	let parsed: object
	switch(encoding) {
		case LdbBlobStorageEncoding.Text:
			let unformatted: string
			if(_.isString(data)) {
				unformatted = data
			} else {
				unformatted = new TextDecoder().decode(data)
			}
			try {
				parsed = JSON.parse(unformatted)
			} catch(ex) {
				return unformatted
			}
			break
		default:
			parsed = serializeFusionError('ldbReadBlobPrettyJson', 'binary preview not available')
	}
	return JSON.stringify(parsed, null, 4)
}

interface ldbWriteBlobDataOk {
	data: string|Uint8Array
	encoding: LdbBlobStorageEncoding
}

export const ldbWriteBlobData = (data: Uint8Array, bin: boolean): Result<ldbWriteBlobDataOk, FusionError> => {
	if(Capacitor.isNativePlatform()) {
		if(bin) {
			try {
				// @ts-ignore .toBase64() only works in Safari on iOS 18.2+
				// but it is very fast
				// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Uint8Array/toBase64
				let b64: string = data.toBase64()
				return ok({
					data: b64,
					encoding: LdbBlobStorageEncoding.B64Bin
				})
			} catch(ex) {
				const error = serializeFusionError('ldbWriteBlobData', ex)
				error.message = 'This error may be due to an old version of iOS; please ensure your device software is up-to-date.  ' + error.message
				return err(error)
			}
		} else {
			try {
				let text: string = new TextDecoder().decode(data)
				return ok({
					data: text,
					encoding: LdbBlobStorageEncoding.Text,
				})
			} catch(ex) {
				return err(serializeFusionError('ldbWriteBlobData', ex))
			}
		}
	}
	return ok({
		data,
		encoding: bin ? LdbBlobStorageEncoding.Bin : LdbBlobStorageEncoding.Text
	})
}
